/**
 * Handles automatic population of document form fields from structured metadata.
 */
define(['core', 'common/component/money-formatter', 'lib/moment'], function (
  core,
  moneyFormatter,
  moment,
) {
  class DocumentAutofill {
    DOCUMENT_TYPE_CONFIGS = {
      STATEMENT: {
        matchTypes: ['creditcardstatement', 'checkingstatement', 'savingsstatement'],
        fields: [
          { from: 'statement_start_date', to: '#openingDate', type: 'date' },
          { from: 'statement_end_date', to: '#closingDate', type: 'date' },
          { from: 'previous_balance', to: '#openingBalance', type: 'money' },
          { from: 'current_balance', to: '#closingBalance', type: 'money' },
        ],
        shouldPrefillLedger: true,
      },

      RECEIPT: {
        matchTypes: ['receipt', 'invoicebill'],
        fields: [
          { from: 'transaction_date', to: '#document-date', type: 'date' },
          { from: 'due_date', to: '#document-date', type: 'date' },
          { from: 'merchant_name', to: '#document-vendor', type: 'text' },
          { from: 'vendor_name', to: '#document-vendor', type: 'text' },
          { from: 'total_amount', to: 'input.amount-value-input', type: 'money' },
        ],
      },

      TAX: {
        matchTypes: ['taxdoc'],
        fields: [
          { from: 'taxpayer_name', to: '#document-name', type: 'text' },
          { from: 'tax_year', to: '#tax-year', type: 'text' },
          { from: 'tax_year', to: '#document-date', type: 'taxYearEndDate' },
          { from: 'form_type', to: '#tax-document-type', type: 'formType' },
        ],
      },
    };

    constructor(model, parentPage) {
      this.model = model;
      this.parentPage = parentPage;
    }

    prefillFromMetadata() {
      try {
        if (!this.model) {
          return;
        }

        const fileObj = this.model.get('file');
        const metadata = fileObj?.get('metadata');
        if (!metadata) {
          return;
        }

        const docType = metadata.document_type?.toLowerCase();
        if (!docType) {
          return;
        }

        const config = this.findDocumentTypeConfig(docType);
        if (!config) {
          return;
        }

        for (const fieldConfig of config.fields) {
          this.prefillField(metadata, fieldConfig);
        }

        // Prefill ledger account if configured
        if (config.shouldPrefillLedger) {
          this.prefillLedgerAccount(metadata);
        }

        const currentType = this.model.get('documentType');
        if (currentType !== config.internalType) {
          this.model.set('documentType', config.internalType);
          this.parentPage?.updateType(config.internalType);
        }
      } catch (error) {
        console.error('Error in prefillFromMetadata:', error);
      }
    }

    findDocumentTypeConfig(docType) {
      try {
        if (!docType) {
          return null;
        }
        for (const internalType of Object.keys(this.DOCUMENT_TYPE_CONFIGS)) {
          const config = this.DOCUMENT_TYPE_CONFIGS[internalType];
          if (config.matchTypes.includes(docType)) {
            return _.extend({}, config, { internalType: internalType });
          }
        }
        return null;
      } catch (error) {
        console.warn('Error finding document type config:', error);
        return null;
      }
    }

    prefillField(metadata, fieldConfig) {
      try {
        if (!metadata || !fieldConfig) {
          return;
        }
        if (!fieldConfig.from || !fieldConfig.to || !fieldConfig.type) {
          return;
        }

        const metadataValue = metadata[fieldConfig.from];
        if (!metadataValue) {
          return;
        }

        const convertedValue = this.convertFieldValue(metadataValue, fieldConfig.type);
        if (!convertedValue) {
          return;
        }

        const input = $(fieldConfig.to);
        if (input?.length > 0) {
          // Handle table inputs (multiple matches - use first)
          input.first().val(convertedValue).change();
        }
      } catch (error) {
        console.warn(`Failed to prefill field ${fieldConfig?.from || 'unknown'}:`, error);
      }
    }

    convertFieldValue(value, type) {
      if (!value) {
        return null;
      }

      try {
        switch (type) {
          case 'date':
            return this.convertDate(value);
          case 'money':
            return isNaN(parseFloat(value)) ? null : moneyFormatter.formatNumber(value);
          case 'text':
            return String(value);
          case 'formType':
            return this.convertFormType(value);
          case 'taxYearEndDate':
            return this.convertTaxYearToEndDate(value);
          default:
            return value;
        }
      } catch (error) {
        console.warn('Failed to convert value:', value, type, error);
        return null;
      }
    }

    // Converts date strings to MM/DD/YYYY format with validation
    // Accepts formats: "MM-DD-YYYY", "MM/DD/YYYY", and other common date formats
    convertDate(dateStr) {
      if (!dateStr) {
        return null;
      }
      try {
        const m = moment(
          dateStr,
          [
            'MM-DD-YYYY',
            'MM/DD/YYYY',
            'YYYY-MM-DD',
            'M-D-YYYY',
            'M/D/YYYY',
            'MMM-DD-YYYY',
            'MMM/DD/YYYY',
            'DD-MMM-YYYY',
          ],
          true,
        );

        if (m.isValid()) {
          return m.format('MM/DD/YYYY');
        }

        // If strict parsing fails, try lenient parsing as fallback
        const lenient = moment(dateStr);
        if (lenient.isValid()) {
          return lenient.format('MM/DD/YYYY');
        }

        return null;
      } catch (error) {
        console.warn('Failed to convert date:', dateStr, error);
        return null;
      }
    }

    FORM_TYPE_MAP = {
      1120: 'FORM_1120',
      '1120S': 'FORM_1120S',
      1040: 'FORM_1040',
      '1040SCHEDULEC': 'FORM_1040_SCHEDULE_C',
      1065: 'FORM_1065',
    };

    convertFormType(formType) {
      if (!formType) {
        return 'UNDEFINED';
      }

      const input = String(formType).toUpperCase().trim();

      // 1099 variants (1099, 1099G, 1099K, 1099-K, etc.) map to SUPPLEMENTAL
      if (input.includes('1099')) {
        return 'SUPPLEMENTAL';
      }

      // Normalize by removing "FORM" prefix and all separators (spaces, hyphens, underscores)
      // Examples: "Form 1120-S" -> "1120S", "1040 Schedule C" -> "1040SCHEDULEC"
      const normalized = input.replace(/^FORM[\s_-]*/i, '').replace(/[\s_-]/g, '');

      return this.FORM_TYPE_MAP[normalized] || 'UNDEFINED';
    }

    convertTaxYearToEndDate(taxYear) {
      const year = parseInt(taxYear, 10);
      if (isNaN(year)) {
        return moment().format('MM/DD/YYYY');
      }
      return `12/31/${year}`;
    }

    extractLastFourDigits(accountNumber) {
      if (!accountNumber) {
        return null;
      }
      const digits = accountNumber.replace(/\D/g, '');
      if (digits.length >= 4) {
        return digits.slice(-4);
      }
      return digits.length > 0 ? digits : null;
    }

    extractAllAccountNumbers(ledgerAccountName) {
      if (!ledgerAccountName) {
        return [];
      }
      const matches = ledgerAccountName.match(/\b\d{4,5}\b/g);
      return matches ? matches.map(num => num.slice(-4)) : [];
    }

    mapDocumentTypeToRole(documentType) {
      if (!documentType) {
        return null;
      }
      const mapping = {
        creditcardstatement: 'CREDIT_CARD',
        checkingstatement: 'BANK_ACCOUNT',
        savingsstatement: 'BANK_ACCOUNT',
      };
      return mapping[documentType.toLowerCase()] || null;
    }

    filterByRole(accounts, role) {
      if (!role) {
        return accounts;
      }
      const filtered = accounts.filter(account => account.role === role);
      return filtered.length > 0 ? filtered : null;
    }

    filterByLastFourDigits(accounts, lastFour) {
      if (!lastFour) {
        return accounts;
      }
      const filtered = accounts.filter(account => {
        if (account.accountLastFourDigits === lastFour) {
          return true;
        }
        const numbersInName = this.extractAllAccountNumbers(account.name);
        return numbersInName.includes(lastFour);
      });
      return filtered.length > 0 ? filtered : null;
    }

    selectBestByBankName(accounts, bankName) {
      if (accounts.length === 1) {
        return accounts[0];
      }
      if (!bankName || typeof window.LiquidMetal === 'undefined') {
        return accounts[0];
      }

      let bestMatch = null;
      let bestScore = 0;
      accounts.forEach(account => {
        const institutionName = account.financialInstitutionProduct?.financialInstitution?.name;
        if (institutionName) {
          const score = window.LiquidMetal.score(institutionName, bankName);
          if (score > bestScore) {
            bestScore = score;
            bestMatch = account;
          }
        }
      });
      return bestMatch || accounts[0];
    }

    findBestMatchingAccount(accounts, metadata) {
      const role = this.mapDocumentTypeToRole(metadata.document_type);
      let candidates = this.filterByRole(accounts, role);
      if (!candidates) {
        return null;
      }

      const { bank_name, account_number } = metadata;
      const lastFour = this.extractLastFourDigits(account_number);

      candidates = this.filterByLastFourDigits(candidates, lastFour);
      if (!candidates) {
        return null;
      }

      return this.selectBestByBankName(candidates, bank_name);
    }

    setLedgerAccountValue(accountName) {
      if (!accountName) {
        return;
      }

      const flexselectInput = $('#ledgerAccountId_flexselect');
      const hiddenField = $('#ledgerAccountId');
      if (flexselectInput.length === 0 || hiddenField.length === 0) {
        return;
      }

      // Find the matching ledger account to get its ID
      const ledgerAccounts = core.getCollection('ledgerAccounts');
      if (!ledgerAccounts) {
        return;
      }

      const matchingAccount = ledgerAccounts.findWhere({ name: accountName });
      if (!matchingAccount) {
        return;
      }

      const accountId = matchingAccount.get('id');

      // Delay slightly to ensure flexselect plugin is fully initialized
      setTimeout(() => {
        // Set both the visible input (display) and hidden field (actual value)
        flexselectInput.val(accountName);
        flexselectInput.data('origin-text', accountName);
        hiddenField.val(accountId);
        hiddenField.data('origin-value', accountId);

        // Trigger events to notify listeners (matching flexselect's pickSelected behavior)
        hiddenField.trigger('change');
        flexselectInput.trigger('dropdownPicked');
      }, 100);
    }

    prefillLedgerAccount(metadata) {
      try {
        if (!metadata || typeof core === 'undefined' || !core.getCollection) {
          return;
        }

        const ledgerAccounts = core.getCollection('ledgerAccounts');
        if (!ledgerAccounts) {
          return;
        }

        const accountsWithRole = ledgerAccounts.toJSON().filter(account => account.role);
        if (accountsWithRole.length === 0) {
          return;
        }

        const match = this.findBestMatchingAccount(accountsWithRole, metadata);
        if (match?.name) {
          this.setLedgerAccountValue(match.name);
        }
      } catch (error) {
        console.warn('Failed to pre-fill LedgerAccount', error);
      }
    }
  }

  return DocumentAutofill;
});
